import gql from 'graphql-tag';

import { Mutation, Query } from 'core/index';

import { USER_ERROR_FIELDS } from 'core/errors';

export const GROUP_PROFILE_FIELDS = gql`
  fragment groupProfileFields on Profile {
    id
    username
    fullName
    imageFallbackChar
    user {
      id
      username
    }
    avatar {
      id
      path
      url
      completed
    }
  }
`;

export const GROUP_FIELDS = gql`
  ${GROUP_PROFILE_FIELDS}
  fragment groupFields on Group {
    id
    name
    canUpdate
    canDelete
    canAddMember
    space {
      id
    }
    parent {
      id
      name
    }
    pendingInvites {
      id
      inviteDate
      email
      status
      profile {
        ...groupProfileFields
      }
      group {
        id
        spaceId
        name
      }
    }
    members {
      id
      canUpdate
      canDelete
      intrinsic
      profile {
        ...groupProfileFields
      }
    }
  }
`;

const GROUPS_QUERY = gql`
  ${GROUP_FIELDS}
  query Groups($spaceSlug: String, $slug: String) {
    groups(spaceSlug: $spaceSlug, slug: $slug) {
      ...groupFields
    }
  }
`;
export class GroupsQuery extends Query {
  static conf = {
    GQL: GROUPS_QUERY,
    varNames: ['spaceSlug', 'slug'],
    resultPath: 'groups',
  };
}

const INVITE_USERS_TO_GROUP = gql`
  ${USER_ERROR_FIELDS}
  ${GROUP_FIELDS}
  mutation InviteUsersToGroup($userGroupInput: UserGroupInviteInput) {
    inviteUsersToGroup(userGroupInput: $userGroupInput) {
      group {
        ...groupFields
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;
export class InviteUsersToGroup extends Mutation {
  static conf = {
    GQL: INVITE_USERS_TO_GROUP,
    inputName: 'userGroupInput',
    resultPath: 'inviteUsersToGroup.group',
    errorPath: 'inviteUsersToGroup.userErrors',
  };
}

const REMOVE_USER_FROM_GROUP = gql`
  ${USER_ERROR_FIELDS}
  ${GROUP_FIELDS}
  mutation RemoveUserFromGroup($userGroupInput: UserGroupActionInput) {
    removeUserFromGroup(userGroupInput: $userGroupInput) {
      group {
        ...groupFields
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;
export class RemoveUserFromGroup extends Mutation {
  static conf = {
    GQL: REMOVE_USER_FROM_GROUP,
    inputName: 'userGroupInput',
    resultPath: 'removeUserFromGroup.group',
    errorPath: 'removeUserFromGroup.userErrors',
  };
}

const MOVE_USER_TO_GROUP = gql`
  ${GROUP_FIELDS}
  ${USER_ERROR_FIELDS}
  mutation MoveGroupMember($moveGroupMemberInput: MoveGroupMemberInput) {
    moveGroupMember(moveGroupMemberInput: $moveGroupMemberInput) {
      result
      fromGroup {
        ...groupFields
      }
      toGroup {
        ...groupFields
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class MoveUserToGroup extends Mutation {
  static conf = {
    GQL: MOVE_USER_TO_GROUP,
    inputName: 'moveGroupMemberInput',
    resultPath: 'moveGroupMember',
    errorPath: 'moveGroupMember.userErrors',
  };
}

const REMOVE_INVITE = gql`
  ${USER_ERROR_FIELDS}
  ${GROUP_FIELDS}
  mutation DeleteInvite($data: DeleteInviteInput) {
    deleteInvite(data: $data) {
      group {
        ...groupFields
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class RemoveInvite extends Mutation {
  static conf = {
    GQL: REMOVE_INVITE,
    inputName: 'data',
    resultPath: 'deleteInvite.group',
    errorPath: 'deleteInvite.userErrors',
  };
}

const INVITE_USER_TO_SIGNUP = gql`
  ${USER_ERROR_FIELDS}
  mutation InviteUsersToSignup($newInvitesInput: SignupInviteInput) {
    inviteUsersToSignup(userGroupInput: $newInvitesInput) {
      result
      invites {
        id
        status
        email
      }
      invitesLeft
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

const MOVE_INVITE_TO_GROUP = gql`
  ${GROUP_FIELDS}
  ${USER_ERROR_FIELDS}
  mutation MoveGroupInvite($moveGroupInviteInput: MoveGroupInviteInput) {
    moveGroupInvite(moveGroupInviteInput: $moveGroupInviteInput) {
      result
      fromGroup {
        ...groupFields
      }
      toGroup {
        ...groupFields
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class MoveInviteToGroup extends Mutation {
  static conf = {
    GQL: MOVE_INVITE_TO_GROUP,
    inputName: 'moveGroupInviteInput',
    resultPath: 'moveGroupInvite',
    errorPath: 'moveGroupInvite.userErrors',
  };
}

export class InviteUserToSignUpMutation extends Mutation {
  static conf = {
    GQL: INVITE_USER_TO_SIGNUP,
    inputName: 'newInvitesInput',
    errorPath: 'inviteUsersToSignup.userErrors',
  };
}

const SIGN_UP_INVITES = gql`
  query SignUpInvites {
    signupInvites {
      id
      status
      email
      moreInvitesRequested
      invitesLeft
    }
  }
`;

export class SignUpInvitesQuery extends Query {
  static conf = {
    GQL: SIGN_UP_INVITES,
    resultPath: 'signupInvites',
  };
}

const REQUEST_MORE_INVITES = gql`
  ${USER_ERROR_FIELDS}
  mutation RequestMoreInvites($requestInvitesInput: InvitesRequestInput) {
    requestMoreInvites(requestInput: $requestInvitesInput) {
      result
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class RequestInvitesMutation extends Mutation {
  static conf = {
    GQL: REQUEST_MORE_INVITES,
    inputName: 'requestInvitesInput',
    errorPath: 'inviteUsersToSignup.userErrors',
  };
}

const SIGN_UP_REQUEST = gql`
  ${USER_ERROR_FIELDS}
  mutation SignUpRequest($inviteRequestInput: SignupRequestInput) {
    requestInvite(requestInput: $inviteRequestInput) {
      result
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class SignUpRequestMutation extends Mutation {
  static conf = {
    GQL: SIGN_UP_REQUEST,
    inputName: 'inviteRequestInput',
    errorPath: 'requestInvite.userErrors',
  };
}

const ACCEPT_INVITE = gql`
  ${USER_ERROR_FIELDS}
  mutation AcceptInvite($data: AcceptInviteInput) {
    acceptInvite(data: $data) {
      result
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class AcceptInvite extends Mutation {
  static conf = {
    GQL: ACCEPT_INVITE,
    inputName: 'data',
    errorPath: 'acceptInvite.userErrors',
  };
}

const REJECT_INVITE = gql`
  ${USER_ERROR_FIELDS}
  mutation RejectInvite($data: RejectInviteInput) {
    rejectInvite(data: $data) {
      result
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class RejectInvite extends Mutation {
  static conf = {
    GQL: REJECT_INVITE,
    inputName: 'data',
    errorPath: 'rejectInvite.userErrors',
  };
}

const INITIATIVE_INVITE_LINK = gql`
  query InitiativeInviteLink($spaceSlug: String) {
    initiative(slug: $spaceSlug) {
      result {
        id
        inviteLink {
          id
          url
          expiresAt
        }
      }
    }
  }
`;

export class InitiativeInvLinkQuery extends Query {
  static conf = {
    GQL: INITIATIVE_INVITE_LINK,
    varNames: ['spaceSlug', 'slug'],
    resultPath: 'initiative.result.inviteLink',
  };
}

const PROJECT_INVITE_LINK = gql`
  ${USER_ERROR_FIELDS}
  query ProjectInviteLink($spaceSlug: String, $slug: String) {
    project(space: $spaceSlug, slug: $slug) {
      result {
        id
        inviteLink {
          id
          url
          expiresAt
        }
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class ProjectInvLinkQuery extends Query {
  static conf = {
    GQL: PROJECT_INVITE_LINK,
    varNames: ['spaceSlug', 'slug'],
    resultPath: 'project.result.inviteLink',
  };
}

const CREATE_INVITE_LINK = gql`
  ${USER_ERROR_FIELDS}
  mutation CreateInviteLink($data: CreateInviteLinkInput) {
    createInviteLink(data: $data) {
      inviteLink {
        id
        url
        expiresAt
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class CreateInviteLink extends Mutation {
  static conf = {
    GQL: CREATE_INVITE_LINK,
    inputName: 'data',
    errorPath: 'createInviteLink.userErrors',
  };
}

const DELETE_INVITE_LINK = gql`
  ${USER_ERROR_FIELDS}
  mutation DeleteInviteLink($data: DeleteInviteLinkInput) {
    deleteInviteLink(data: $data) {
      result
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class DeleteInviteLink extends Mutation {
  static conf = {
    GQL: DELETE_INVITE_LINK,
    inputName: 'data',
    errorPath: 'createInviteLink.userErrors',
  };
}

const INVITE_QUERY = gql`
  ${USER_ERROR_FIELDS}
  query InviteLink(
    $token: String!
    $inviteLinkId: String!
    $referrerUserId: String!
  ) {
    inviteLink(
      token: $token
      inviteLinkId: $inviteLinkId
      referrerUserId: $referrerUserId
    ) {
      result(
        token: $token
        inviteLinkId: $inviteLinkId
        referrerUserId: $referrerUserId
      ) {
        id
        content {
          id
          title
          description
          avatar {
            id
            path
            url
          }
          inSpace {
            id
            spaceType
            parent {
              id
              spaceType
              content {
                id
                slug
              }
            }
          }
          slug
          parentSlug
          isPrivate
          imageFallbackChar
          type
        }
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class InviteQuery extends Query {
  static conf = {
    GQL: INVITE_QUERY,
    varNames: ['token', 'inviteLinkId', 'referrerUserId'],
    resultPath: 'inviteLink.result',
  };
}
